import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { ApiError, type Me } from "../api.ts";
import { useScopedApi } from "../use-scoped-api.ts";
import { usePendingActions } from "../pending-actions.ts";
import { cn } from "../lib/utils.ts";
import { ConsoleSection, StatusBadge } from "./product.tsx";
import { Button } from "./ui/button.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "./ui/alert-dialog.tsx";

const scopeLabels = {
  "appnotes:read": "Read feedback",
  "appnotes:write": "Write feedback",
  "appnotes:sites:create": "Register sites",
} as const;
const pageSchema = z.object({
  can_reset_sign_in: z.boolean().default(false),
  connections: z.array(
    z.object({
      id: z.string(),
      generation: z.number().int().positive(),
      client_id: z.string(),
      client_name: z.string(),
      organization_id: z.string(),
      organization_name: z.string().nullable(),
      scopes: z.array(
        z.enum(["appnotes:read", "appnotes:write", "appnotes:sites:create"]),
      ),
      created_at: z.iso.datetime(),
      last_used_at: z.iso.datetime().nullable(),
      revoked_at: z.iso.datetime().nullable(),
    }),
  ),
  next_after: z.string().nullable(),
});
const resetSchema = z.object({
  status: z.enum(["complete", "retry", "pending", "changed"]),
  operation_id: z.string().nullable(),
});
type Connection = z.infer<typeof pageSchema>["connections"][number];
type Inventory =
  | { readonly kind: "loading" }
  | { readonly kind: "error"; readonly message: string }
  | { readonly kind: "ready"; readonly page: z.infer<typeof pageSchema> };
const buttonClass =
  "transition-transform active:scale-[0.96] motion-reduce:active:scale-100";

/** Personal browser approvals remain reachable even when the person has no team. */
export function ConnectedAgents({ me }: { readonly me: Me }) {
  const api = useScopedApi(me.user.id);
  const [inventory, setInventory] = useState<Inventory>({ kind: "loading" });
  const [pages, setPages] = useState<readonly (string | null)[]>([null]);
  const [revision, setRevision] = useState(0);
  const [notice, setNotice] = useState("");
  const inventoryRead = useRef(0);
  const after = pages.at(-1) ?? null;
  const endpoint = `/v1/me/mcp-connections${after === null ? "" : `?after=${encodeURIComponent(after)}`}`;
  async function readPage(signal?: AbortSignal) {
    const response = await api<unknown>(
      endpoint,
      signal === undefined ? {} : { signal },
    );
    const parsed = pageSchema.safeParse(response);
    if (!parsed.success)
      throw new ApiError(
        "The connection list could not be read. Please try again.",
        0,
        "invalid_response",
      );
    return parsed.data;
  }
  useEffect(() => {
    const controller = new AbortController();
    const read = ++inventoryRead.current;
    setNotice("");
    setInventory({ kind: "loading" });
    void readPage(controller.signal)
      .then((page) => {
        if (!controller.signal.aborted && read === inventoryRead.current)
          setInventory({ kind: "ready", page });
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted && read === inventoryRead.current)
          setInventory({ kind: "error", message: message(cause) });
      });
    return () => controller.abort();
  }, [api, endpoint, revision]);

  async function revoke(connection: Connection): Promise<void> {
    const read = ++inventoryRead.current;
    setNotice("");
    await api<void>(
      `/v1/me/mcp-connections/${encodeURIComponent(connection.id)}`,
      {
        method: "DELETE",
        csrf: me.csrf_token,
        body: { generation: connection.generation },
      },
    );
    // Confirm current state after a lost response or concurrent reconnection.
    // Never optimistically mark a stale generation as revoked.
    const page = await readPage();
    if (read === inventoryRead.current) setInventory({ kind: "ready", page });
    const current = page.connections.find((item) => item.id === connection.id);
    if (current !== undefined && current.revoked_at === null)
      throw new ApiError(
        "This connection changed. Close this dialog and review it before revoking again.",
        409,
        "connection_changed",
      );
    setNotice(
      `${connection.client_name || connection.client_id} access revoked. Existing notes are unchanged.`,
    );
  }
  async function reset(connection: Connection): Promise<void> {
    const read = ++inventoryRead.current;
    setNotice("");
    const response = resetSchema.safeParse(
      await api<unknown>(
        `/v1/me/mcp-connections/${encodeURIComponent(connection.id)}/reset-sign-in`,
        {
          method: "POST",
          csrf: me.csrf_token,
          body: { generation: connection.generation },
        },
      ),
    );
    if (!response.success)
      throw new ApiError(
        "The reset response could not be read. Check its status again.",
        0,
        "invalid_response",
      );
    const page = await readPage();
    if (read === inventoryRead.current) setInventory({ kind: "ready", page });
    const result = response.data;
    if (result.status === "changed")
      throw new ApiError(
        "This connection changed. Close this dialog and review its current state.",
        409,
        "connection_changed",
      );
    if (result.status === "retry")
      throw new ApiError(
        "Access is blocked. The sign-in provider could not be reached. Try the reset again.",
        503,
        "reset_retry",
      );
    if (result.status === "pending")
      throw new ApiError(
        `Access is blocked. The provider reset is not confirmed. Check its status again; if this persists, contact support with reference ${result.operation_id ?? "unavailable"}.`,
        503,
        "reset_pending",
      );
    setNotice(
      "Client sign-in reset. Sign out of this MCP connection in your coding client, then connect again and approve access in AppNotes. Existing notes are unchanged.",
    );
  }
  return (
    <ConsoleSection
      title="Connected agents"
      titleId="connected-agents-title"
      actions={
        <Button
          className={buttonClass}
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setRevision((n) => n + 1)}
        >
          {inventory.kind === "error" ? "Retry connections" : "Refresh"}
        </Button>
      }
    >
      <p className="px-5 pt-4 pb-2 text-sm text-pretty text-muted-foreground">
        Connections you approved across teams. Revoke to stop access; signing
        out does not disconnect agents.
      </p>
      <p className="px-5 text-sm" role="status">
        {notice}
      </p>
      {inventory.kind === "loading" ? (
        <div
          className="grid gap-3 p-5"
          role="status"
          aria-label="Loading connected agents"
        >
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : inventory.kind === "error" ? (
        <div className="grid justify-items-start gap-3 p-5">
          <p role="alert">{inventory.message}</p>
        </div>
      ) : inventory.page.connections.length === 0 ? (
        <div className="grid gap-2 p-5">
          <p className="font-semibold">No connected agents yet.</p>
          <p className="text-sm text-muted-foreground">
            Add this deployment’s MCP URL in your coding client, then sign in to
            AppNotes to approve it.
          </p>
          <code className="break-all text-xs select-text">{`${window.location.origin}/mcp`}</code>
        </div>
      ) : (
        <ul className="divide-y">
          {inventory.page.connections.map((connection) => (
            <ConnectionRow
              key={connection.id}
              connection={connection}
              revoke={revoke}
              reset={inventory.page.can_reset_sign_in ? reset : undefined}
            />
          ))}
        </ul>
      )}
      {(pages.length > 1 ||
        (inventory.kind === "ready" && inventory.page.next_after !== null)) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t p-5">
          <Button
            className={buttonClass}
            variant="outline"
            size="sm"
            disabled={pages.length === 1 || inventory.kind === "loading"}
            onClick={() => setPages((current) => current.slice(0, -1))}
          >
            Previous
          </Button>
          <span className="text-sm tabular-nums">Page {pages.length}</span>
          <Button
            className={buttonClass}
            variant="outline"
            size="sm"
            disabled={
              inventory.kind !== "ready" || inventory.page.next_after === null
            }
            onClick={() => {
              if (
                inventory.kind === "ready" &&
                inventory.page.next_after !== null
              )
                setPages((current) => [...current, inventory.page.next_after]);
            }}
          >
            Next
          </Button>
        </div>
      )}
    </ConsoleSection>
  );
}

function ConnectionRow({
  connection: c,
  revoke,
  reset,
}: {
  readonly connection: Connection;
  readonly revoke: (connection: Connection) => Promise<void>;
  readonly reset: ((connection: Connection) => Promise<void>) | undefined;
}) {
  // Keep the approved target stable across refreshes and ambiguous retries.
  // A new grant generation requires closing and reopening confirmation.
  const [selection, setSelection] = useState<Connection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = usePendingActions();
  const title = useRef<HTMLHeadingElement>(null);
  const busy = pending.isPending("revoke");
  const resetting = (selection ?? c).revoked_at !== null;
  async function confirm(): Promise<void> {
    if (selection === null) return;
    await pending.run("revoke", async () => {
      setError(null);
      try {
        if (selection.revoked_at !== null && reset !== undefined)
          await reset(selection);
        else await revoke(selection);
        setSelection(null);
      } catch (cause) {
        setError(message(cause));
      }
    });
  }
  return (
    <li className="grid min-w-0 gap-4 p-5 sm:grid-cols-[minmax(0,1fr)_auto]">
      <div className="grid min-w-0 gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h3
            ref={title}
            tabIndex={-1}
            className="min-w-0 break-words text-base font-semibold"
          >
            {c.client_name || "Coding client"}
          </h3>
          <StatusBadge>
            {c.revoked_at !== null
              ? "Revoked"
              : c.organization_name === null
                ? "No team access"
                : "Approved"}
          </StatusBadge>
        </div>
        <p className="break-all font-mono text-xs text-muted-foreground">
          {c.client_id}
        </p>
        <p className="text-sm font-medium">
          {c.organization_name ?? "Former team (no current membership)"}
        </p>
        <p className="text-sm text-muted-foreground">
          {`${c.scopes.map((scope) => scopeLabels[scope]).join(" · ")}. ${
            c.organization_name === null
              ? "This connection cannot access a team you no longer belong to."
              : "Your current team permissions still apply."
          }`}
        </p>
        <dl className="grid gap-x-6 gap-y-2 text-xs text-muted-foreground sm:grid-cols-2">
          <div>
            <dt>First connected</dt>
            <dd>
              <time dateTime={c.created_at}>{date(c.created_at)}</time>
            </dd>
          </div>
          <div>
            <dt>Last used (approx.)</dt>
            <dd>
              {c.last_used_at === null ? (
                "Not used yet"
              ) : (
                <time dateTime={c.last_used_at}>{date(c.last_used_at)}</time>
              )}
            </dd>
          </div>
        </dl>
      </div>
      <AlertDialog
        open={selection !== null}
        onOpenChange={(next) => {
          if (!busy) {
            setSelection(next ? c : null);
            setError(null);
          }
        }}
      >
        {(c.revoked_at === null || reset !== undefined) && (
          <AlertDialogTrigger
            render={
              <Button
                className={cn(buttonClass, "justify-self-start")}
                size="sm"
                variant="outline"
              />
            }
          >
            {c.revoked_at === null ? "Revoke" : "Reset client sign-in"}
          </AlertDialogTrigger>
        )}
        <AlertDialogContent finalFocus={title}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {resetting
                ? "Reset client sign-in?"
                : `Revoke ${(selection ?? c).client_name || "this agent"}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {resetting
                ? "This clears your WorkOS authorization for this coding client, including its access across AppNotes teams and any other resources authorized through the same provider application. Other people’s connections and existing notes are unchanged. After the reset, connect again in your coding client and approve the access you want."
                : `This stops this client’s access to ${(selection ?? c).organization_name ?? "its former team"} through your approval. It does not delete notes or affect anyone else’s connections. To reconnect later, reset the client sign-in here first.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error !== null && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button
              className={buttonClass}
              variant="destructive"
              aria-disabled={busy}
              aria-busy={busy}
              onClick={() => void confirm()}
            >
              {busy
                ? resetting
                  ? "Checking reset…"
                  : "Revoking…"
                : resetting
                  ? error === null
                    ? "Reset client sign-in"
                    : "Check reset status"
                  : "Revoke access"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}
function message(cause: unknown): string {
  return cause instanceof ApiError
    ? cause.message
    : "AppNotes could not update your connections. Please try again.";
}
function date(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
